// DesktopLayout.js
import React, { useEffect, useState } from "react";
import TextTools from "../components/TextTools";
import Assets from "../components/Assets";
import DeepFry from "../components/DeepFry";
import Eraser from "../components/Eraser";
import Background from "../components/Background";
import EmptyCanvasOverlay from "../components/InstructionsOverlay";
import TabButtons from "../components/TabButtons";
import HelpModal from "../components/HelpModal";
import Layers from "../components/Layers";

import dlicon from "../assets/icons/dlicon.png";
import deleteicon from "../assets/icons/delete.png";
import up from "../assets/icons/up.png";
import down from "../assets/icons/down.png";
import reset from "../assets/icons/reset.png";
import questionIcon from "../assets/icons/question-mark.png";
import flip from "../assets/icons/flip.png";

import { downloadMemeHighRes } from "../helpers/downloadMeme";
import { bringLayerForward, sendLayerBackward } from "../helpers/moveLayers";
import { resetCanvas } from "../helpers/resetCanvas";

function DesktopLayout({
	// Props we expect:
	canvasRef,
	uploadedImage,
	uploadedImageObj,
	bgImage,
	setBgImage,
	activeTab,
	setActiveTab,
	isMobile, // Typically false here because we only render this if !isMobile
	layers,
	drawCanvas,
	updateAnySelected,
	selectedTextIsWhite,
	setSelectedTextIsWhite,
	selectedFontFamily,
	setSelectedFontFamily,
	isBold,
	setIsBold,
	isItalic,
	setIsItalic,
	isUnderlined,
	setIsUnderlined,
	isStrikethrough,
	setIsStrikethrough,
	isAllCaps,
	setIsAllCaps,
	textOpacity,
	setTextOpacity,
	clampTextInsideBgRect,
	topTextInput,
	setTopTextInput,
	bottomTextInput,
	setBottomTextInput,
	extraTextInputs,
	setExtraTextInputs,
	handleSetBackgroundFromUrl,
	handleAddNftAsAsset,
	handleRemoveBackground,
	handleImageUpload,
	category,
	setCategory,
	isAssetAdded,
	setIsAssetAdded,
	originalBgDataRef,
	preDeepFryCanvasRef,
	preDeepFryLayersRef,
	anySelected,
	handleDeleteSelected,
	isErasing,
	setIsErasing,
	eraserSize,
	setEraserSize,
	handleUndoLastErase,
	handleClearAllErases,
	eraserStrokesRef,
	bgRect,
	satValue,
	setSatValue,
	conValue,
	setConValue,
	briValue,
	setBriValue,
	hueValue,
	setHueValue,
	pixValue,
	setPixValue,
	noiseValue,
	setNoiseValue,
	hasDeepFried,
	setHasDeepFried,
	satEnabled,
	setSatEnabled,
	conEnabled,
	setConEnabled,
	briEnabled,
	setBriEnabled,
	hueEnabled,
	setHueEnabled,
	pixEnabled,
	setPixEnabled,
	noiseEnabled,
	setNoiseEnabled,
	setUploadedImage,
	forceUpdateLayers,
	layersVersion,
}) {
	const [showResetWarning, setShowResetWarning] = useState(false);
	const [canvasRect, setCanvasRect] = useState(null);

	const [showHelp, setShowHelp] = useState(false);

	// Measure the canvas so we can overlay the confirmation dialog
	useEffect(() => {
		if (canvasRef?.current) {
			const rect = canvasRef.current.getBoundingClientRect();
			setCanvasRect(rect);
		}
	}, [canvasRef]);

	// Whenever activeTab changes, disable erasing if we’re no longer on the “eraser” tab
	useEffect(() => {
		if (activeTab !== "eraser") {
			setIsErasing(false);
		}
	}, [activeTab, setIsErasing]);

	const handleDownloadMeme = async () => {
		// Reuse the exact same logic
		await downloadMemeHighRes({
			uploadedImageObj,
			layers,
			bgRect,
		});
	};

	const handleBringForward = () => {
		bringLayerForward(layers, drawCanvas, forceUpdateLayers);
	};

	const handleSendBackward = () => {
		sendLayerBackward(layers, drawCanvas, forceUpdateLayers);
	};

	const handleFlipSelected = () => {
		// Find the currently-selected layer
		const selectedLayer = layers.current.find((l) => l.isSelected);
		if (!selectedLayer) return;

		// If it's an asset, toggle its flipped state
		if (selectedLayer.type === "asset") {
			selectedLayer.isFlipped = !selectedLayer.isFlipped;
			drawCanvas();
		}
	};

	// 4) Show/hide the Reset confirmation
	const handleShowResetConfirm = () => {
		setShowResetWarning(true);
	};
	const handleConfirmReset = () => {
		resetCanvas({
			setUploadedImage,
			setBgImage,
			uploadedImageObj,
			originalBgDataRef,
			layersRef: layers,
			eraserStrokesRef,
			setTopTextInput,
			setBottomTextInput,
			setExtraTextInputs,
			setIsAssetAdded,
			canvasRef,
		});
		setShowResetWarning(false);
	};
	const handleCancelReset = () => {
		setShowResetWarning(false);
	};

	{
		return (
			<div className="relative w-full min-w-[1400px]">
				{/* 2) Place your logo in the top-left corner */}
				<div className="absolute top-0 left-0 pl-4">
				<a href="https://hornsworld.xyz" target="_blank" rel="noopener noreferrer">

					<img
						src="https://d39nah1etu3fy9.cloudfront.net/images/assets/sealuminati/hornelius_no_bg.png"
						alt="Hornsworld Logo"
						className="max-w-[8vw] min-w-[120px] h-auto"
					/>
					</a>
				</div>
				<div className="flex flex-row items-start justify-center w-full overflow-x-hidden mt-12">
					{/* ========== CANVAS AREA ========== */}
					<div className="relative border-2 border-dashed border-monadGray p-2">
						<div className="absolute top-2 -left-16 flex flex-col gap-8 z-10 items-center justify-center ">
							{/* Download Button */}
							<button
								onClick={handleDownloadMeme}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125
										disabled:opacity-50 disabled:cursor-not-allowed"
								title="Download"
								disabled={!bgImage}
							>
								<img src={dlicon} alt="Download Meme" className="w-12 h-12" />
							</button>

							{/* Delete Button */}
							<button
								onClick={handleDeleteSelected}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125 mr-2
										disabled:opacity-50 disabled:cursor-not-allowed"
								title="Delete Selected Item"
								disabled={!bgImage}
							>
								<img src={deleteicon} alt="Delete Selected" className="w-9 h-9" />
							</button>
							{/* Layer Up Button */}
							<button
								onClick={handleBringForward}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125
										disabled:opacity-50 disabled:cursor-not-allowed"
								title="Bring Selected Item Up a Layer"
								disabled={!bgImage}
							>
								<img src={up} alt="Layer Up" className="w-12 h-12" />
							</button>

							{/* Layer Down Button */}
							<button
								onClick={handleSendBackward}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125
										disabled:opacity-50 disabled:cursor-not-allowed"
								title="Send Selected ITem Down a Layer"
								disabled={!bgImage}
							>
								<img src={down} alt="Layer Down" className="w-12 h-12" />
							</button>
							{/* Flip */}
							<button
								onClick={handleFlipSelected}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125 mr-3
										disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={!bgImage}
								title="Flip Selected Asset Horizontally"
							>
								<img src={flip} alt="Flip Item" className="w-12 h-12" />
							</button>
							{/* Reset Button */}
							<button
								onClick={handleShowResetConfirm}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125 mr-3
										disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={!bgImage}
								title="Reset Canvas"
							>
								<img src={reset} alt="Reset Canvas" className="w-10 h-10" />
							</button>

							{/* Help */}
							<button
								onClick={() => setShowHelp(true)}
								title="Show Help"
								disabled={!bgImage}
								className="p-1 rounded shadow transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<img src={questionIcon} alt="Help / Instructions" className="w-12 h-12 mr-3" />
							</button>
						</div>
						<canvas ref={canvasRef} className="block max-w-full" />
						{!bgImage && <EmptyCanvasOverlay />}
					</div>
					{/* ========== TOOLS (on the right) ========== */}
					<div className="p-4 border-monadGray ml-4 border-l w-[400px]">
						{/* Tab Buttons (e.g. background, text, assets, deepFry, eraser) */}
						<TabButtons activeTab={activeTab} setActiveTab={setActiveTab} />

						{/* Active Tab Content */}
						<div className="mt-2">
							{activeTab === "background" && (
								<Background
									onNftBackground={handleSetBackgroundFromUrl}
									onNftAsset={handleAddNftAsAsset}
									onRemoveBackground={handleRemoveBackground}
									hasBackground={!!bgImage}
									onLocalBgFile={handleImageUpload}
								/>
							)}

							{activeTab === "text" && (
								<TextTools
									canvasRef={canvasRef}
									layers={layers}
									drawCanvas={drawCanvas}
									updateAnySelected={updateAnySelected}
									selectedTextIsWhite={selectedTextIsWhite}
									setSelectedTextIsWhite={setSelectedTextIsWhite}
									selectedFontFamily={selectedFontFamily}
									setSelectedFontFamily={setSelectedFontFamily}
									isBold={isBold}
									setIsBold={setIsBold}
									isItalic={isItalic}
									setIsItalic={setIsItalic}
									isUnderlined={isUnderlined}
									setIsUnderlined={setIsUnderlined}
									isStrikethrough={isStrikethrough}
									setIsStrikethrough={setIsStrikethrough}
									isAllCaps={isAllCaps}
									setIsAllCaps={setIsAllCaps}
									textOpacity={textOpacity}
									setTextOpacity={setTextOpacity}
									clampTextInsideBgRect={clampTextInsideBgRect}
									topTextInput={topTextInput}
									setTopTextInput={setTopTextInput}
									bottomTextInput={bottomTextInput}
									setBottomTextInput={setBottomTextInput}
									extraTextInputs={extraTextInputs}
									setExtraTextInputs={setExtraTextInputs}
									hasBackground={!!bgImage}
									bgRect={bgRect}
									forceUpdateLayers={forceUpdateLayers}
								/>
							)}

							{activeTab === "assets" && (
								<Assets
									canvasRef={canvasRef}
									layers={layers}
									drawCanvas={drawCanvas}
									updateAnySelected={updateAnySelected}
									isMobile={isMobile}
									uploadedImage={uploadedImage}
									category={category}
									setCategory={setCategory}
									isAssetAdded={isAssetAdded}
									setIsAssetAdded={setIsAssetAdded}
									hasBackground={!!bgImage}
									bgRect={bgRect}
								/>
							)}

							{activeTab === "layers" && (
								<Layers
									layersRef={layers}
									drawCanvas={drawCanvas}
									forceUpdateLayers={forceUpdateLayers}
									layersVersion={layersVersion}
									canvasRef={canvasRef}
									bgRect={bgRect}
								/>
							)}

							{activeTab === "deepFry" && (
								<DeepFry
									canvasRef={canvasRef}
									layers={layers}
									drawCanvas={drawCanvas}
									bgRect={bgRect}
									uploadedImage={uploadedImage}
									uploadedImageObj={uploadedImageObj}
									preDeepFryCanvasRef={preDeepFryCanvasRef}
									preDeepFryLayersRef={preDeepFryLayersRef}
									originalBgDataRef={originalBgDataRef}
									satValue={satValue}
									setSatValue={setSatValue}
									conValue={conValue}
									setConValue={setConValue}
									briValue={briValue}
									setBriValue={setBriValue}
									hueValue={hueValue}
									setHueValue={setHueValue}
									pixValue={pixValue}
									setPixValue={setPixValue}
									noiseValue={noiseValue}
									setNoiseValue={setNoiseValue}
									hasBackground={!!bgImage}
									hasDeepFried={hasDeepFried}
									setHasDeepFried={setHasDeepFried}
									satEnabled={satEnabled} setSatEnabled={setSatEnabled}
									conEnabled={conEnabled} setConEnabled={setConEnabled}
									briEnabled={briEnabled} setBriEnabled={setBriEnabled}
									hueEnabled={hueEnabled} setHueEnabled={setHueEnabled}
									pixEnabled={pixEnabled} setPixEnabled={setPixEnabled}
									noiseEnabled={noiseEnabled} setNoiseEnabled={setNoiseEnabled}
								/>
							)}

							{activeTab === "eraser" && (
								<Eraser
									canvasRef={canvasRef}
									layers={layers}
									isMobile={isMobile}
									uploadedImage={uploadedImage}
									uploadedImageObj={uploadedImageObj}
									setUploadedImage={setIsAssetAdded}
									setIsAssetAdded={setIsAssetAdded}
									originalBgDataRef={originalBgDataRef}
									preDeepFryCanvasRef={preDeepFryCanvasRef}
									preDeepFryLayersRef={preDeepFryLayersRef}
									drawCanvas={drawCanvas}
									anySelected={anySelected}
									isAssetAdded={isAssetAdded}
									onDeleteSelected={handleDeleteSelected}
									isErasing={isErasing}
									setIsErasing={setIsErasing}
									eraserSize={eraserSize}
									setEraserSize={setEraserSize}
									onUndoLastErase={handleUndoLastErase}
									onClearAllErases={handleClearAllErases}
									bgImage={bgImage}
									setBgImage={setBgImage}
									hasBackground={!!bgImage}
									eraserStrokesRef={eraserStrokesRef}
									bgRect={bgRect}
									setTopTextInput={setTopTextInput}
									setBottomTextInput={setBottomTextInput}
									setExtraTextInputs={setExtraTextInputs}
								/>
							)}
						</div>
					</div>
					{/* ===== Confirmation Dialog Overlay for Reset Canvas ===== */}
					{showResetWarning && canvasRect && (
						<div
							style={{
								position: "absolute",
								top: canvasRect.top + window.scrollY,
								left: canvasRect.left + window.scrollX,
								width: canvasRect.width,
								height: canvasRect.height,
								zIndex: 999,
							}}
						>
							{/* Center the dialog box inside the overlay */}
							<div
								className="
              absolute
              top-1/2
              left-1/2
              -translate-x-1/2
              -translate-y-1/2
              bg-monadAccent
              rounded-md
              w-[80%]
              max-w-[300px]
              p-6
              text-center
              border-2
              border-monadGray
              shadow-xl
            "
							>
								<h2 className="text-sm md:text-base font-bold mb-4 text-monadWhite">
									Are you sure you want to reset?
								</h2>
								<p className="text-monadWhite text-sm md:text-base mb-4">
									This will erase the current state of the canvas.
								</p>
								<div className="flex gap-4 justify-center">
									<button
										onClick={handleConfirmReset}
										className="
                  text-sm md:text-base px-4 py-2 rounded-md bg-red-600 text-white
                  hover:bg-red-700 transition-colors
                "
									>
										Reset Canvas
									</button>
									<button
										onClick={handleCancelReset}
										className="
                  text-sm md:text-base px-4 py-2 rounded-md bg-gray-300 text-black
                  hover:bg-gray-400 transition-colors
                "
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					)}{" "}
					{showHelp && (
						<HelpModal
							onClose={() => setShowHelp(false)}
							canvasRect={canvasRect} // pass the canvas rect
							isDesktop={true} // or some condition like window.innerWidth >= 1024
						/>
					)}
				</div>
			</div>
		);
	}
}
export default DesktopLayout;
